import { Dispatch, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { AppAction } from '../appState'
import { track } from '../lib/tracking'
import { camelToKebab } from '../lib/utils'

interface Props {
  initialValues: string[]
  appDispatch: Dispatch<AppAction>
  id: string
  validationFunction: (values: string[]) => string
}

export default function useCheckboxForm({
  initialValues,
  appDispatch,
  id,
  validationFunction,
}: Props) {
  const [values, setValues] = useState(initialValues)
  const [errorMessage, setErrorMessage] = useState('')
  const [hasBeenTouched, setHasBeenTouched] = useState(initialValues.length > 0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.querySelector('input')?.focus()
  }, [])

  const onChangeHandler = useCallback(
    (values: string[]) => {
      setHasBeenTouched(true)
      setErrorMessage('')
      track('user-input', camelToKebab(id))
      appDispatch({ type: 'inputValueChange', value: values })
      setValues(values)
    },
    [appDispatch, id],
  )

  const validate = useCallback(() => {
    const newErrorMessage = validationFunction(values)
    setErrorMessage(newErrorMessage)
    return !newErrorMessage
  }, [values, validationFunction])

  const submitHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      track('click', `next-button.${camelToKebab(id)}`)
      if (!validate()) return

      appDispatch({ type: 'goForward' })
    },
    [id, validate, appDispatch],
  )

  return {
    inputRef,
    handledValues: values,
    errorMessage,
    hasBeenTouched,
    onChangeHandler,
    submitHandler,
    validate,
  }
}
